import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import { toast } from "sonner";
import { HardDrive, Check } from "lucide-react";

const fmt = (b) => (b >= 1073741824 ? `${(b / 1073741824).toFixed(0)} GB` : `${(b / 1048576).toFixed(0)} MB`);
const fmtPlan = (b) => (b >= 1099511627776 ? `${(b / 1099511627776).toFixed(0)} TB` : `${(b / 1073741824).toFixed(0)} GB`);

// "Spazio e abbonamento" — riutilizzabile per Utente/Professionista/Struttura.
export function DriveSubscription() {
  const { t } = useTranslation();
  const [cur, setCur] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/drive/subscription").then(({ data }) => setCur(data)).catch(() => {});
    api.get("/drive-plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  };
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sess = p.get("sub_session");
    if (sess) {
      api.get(`/drive/subscription/status/${sess}`).then(({ data }) => {
        if (data.status === "active") toast.success(t("pd.activated")); else toast.info(t("pd.processing"));
      }).catch(() => {}).finally(() => { window.history.replaceState({}, "", window.location.pathname); load(); });
    } else if (p.get("sub") === "cancel") {
      toast.info(t("pd.processing")); window.history.replaceState({}, "", window.location.pathname); load();
    } else load();
  }, []);

  const subscribe = async (planId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/drive/subscribe", { plan_id: planId, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      const code = err.response?.data?.detail?.code || err.response?.data?.detail;
      toast.error(code === "already_subscribed" ? t("pd.already") : t("pd.pay_unavail"));
    } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm(t("pd.cancel_confirm"))) return;
    setBusy(true);
    try { await api.post("/drive/subscription/cancel"); toast.success(t("pd.canceled_ok")); load(); }
    catch { toast.error("Error"); } finally { setBusy(false); }
  };

  if (!cur) return null;
  const usage = cur.usage || {};
  const active = cur.subscription;
  const subEnabled = cur.commerce ? cur.commerce.subscribe_enabled !== false : true;
  const pct = Math.min(100, Math.round((usage.active_used_bytes / (usage.quota_bytes || 1)) * 100));

  return (
    <div className="space-y-5" data-testid="drive-subscription-panel">
      <div className="yp-card p-5">
        <div className="flex items-center gap-2 mb-3"><HardDrive size={18} className="text-blue-600" /><h3 className="font-semibold text-lg">{t("pd.space_title")}</h3></div>
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="text-slate-600">{active ? `${active.plan_code?.toUpperCase()}` : t("pd.plan_free")}</span>
          <span className="text-slate-500" data-testid="drive-sub-usage">{fmt(usage.active_used_bytes || 0)} / {fmt(usage.quota_bytes || 0)}</span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
          <div className={`h-full ${usage.is_over_quota ? "bg-red-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
        </div>
        {active && (
          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm text-slate-600" data-testid="drive-sub-active">{t("pd.sub_active")}: {fmtPlan(active.plan_bytes)} · € {Number(active.price_eur_month).toFixed(2)}{t("pd.per_month")}</span>
            <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs text-red-600" onClick={cancel} disabled={busy} data-testid="drive-sub-cancel">{t("pd.cancel")}</button>
          </div>)}
      </div>

      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">{t("pd.plans_available")}</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="drive-plans-grid">
          {plans.map((p) => {
            const isCurrent = active && active.plan_id === p.id;
            return (
              <div key={p.id} className={`yp-card p-4 flex flex-col ${isCurrent ? "ring-2 ring-blue-500" : ""}`} data-testid="drive-plan-card">
                <div className="font-semibold text-base">{p.name}</div>
                <div className="text-2xl font-bold mt-1">{fmtPlan(p.bytes)}</div>
                <div className="text-slate-500 text-sm mt-0.5">€ {Number(p.price_eur_month).toFixed(2)}{t("pd.per_month")}</div>
                <button className={`yp-btn ${isCurrent ? "yp-btn-ghost" : "yp-btn-primary"} !py-1.5 text-xs mt-3`}
                  onClick={() => subscribe(p.id)} disabled={busy || isCurrent || !subEnabled} data-testid={`drive-plan-subscribe-${p.code}`}>
                  {isCurrent ? (<><Check size={14} className="inline mr-1" />{t("pd.active")}</>) : (!subEnabled ? t("pd.subscribe_disabled") : t("pd.subscribe"))}
                </button>
              </div>);
          })}
          {plans.length === 0 && <div className="text-slate-400 text-sm">{t("pd.no_plans")}</div>}
        </div>
        {!subEnabled && <p className="text-xs text-amber-600 mt-2" data-testid="drive-sub-disabled-note">{t("pd.subscribe_soon")}</p>}
        <p className="text-xs text-slate-400 mt-3">{t("pd.quota_note")}</p>
      </div>
    </div>
  );
}
